import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { HistoryIcon } from "lucide-react";

import { isPaginatedResponse } from "@/redux/typeGuards";
import { DataBlockChangesetResponse } from "@/redux/datablocks/types";
import { PAGINATION } from "@/shared/utils/constants";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import UserAvatar from "@/domains/users/components/UserAvatar";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import { useGetDataBlockHistoryQuery } from "@/redux/service";
import HistoryChangeRow from "./history/HistoryChangeRow";
import { useHistoryRelatedData } from "@/features/datablocks/details/hooks/useHistoryRelatedData";
import { HistoryProps } from "../types";
import { AccordionVariant } from "@/components_v2/Accordion/types";

import "../styles.css";

const History = ({ dataBlockId }: HistoryProps) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [allChangesets, setAllChangesets] = useState<
    DataBlockChangesetResponse[]
  >([]);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isFetching } = useGetDataBlockHistoryQuery({
    id: dataBlockId,
    filters: {
      page,
      pageSize: PAGINATION.PAGE_SIZE.DATA_BLOCK_HISTORY,
    },
  });
  const [latestChangesets, setLatestChangesets] = useState<
    DataBlockChangesetResponse[]
  >([]);
  const { lookup, isLoading: isRelatedLoading } =
    useHistoryRelatedData(latestChangesets);

  useEffect(() => {
    if (!data) return;
    const results = isPaginatedResponse(data) ? data.results : data;
    const next = isPaginatedResponse(data) ? data.next : null;

    setLatestChangesets(results);

    setAllChangesets((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      const newItems = results.filter(
        (c: DataBlockChangesetResponse) => !existingIds.has(c.id),
      );
      return [...prev, ...newItems];
    });
    setHasMore(!!next);
  }, [data]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isFetching) loadMore();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, isFetching]);

  const sortedChangesets = useMemo(() => {
    if (!allChangesets.length) return [];
    return [...allChangesets].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [allChangesets]);

  const accordionItems = useMemo(
    () =>
      sortedChangesets.map((changeset) => ({
        key: String(changeset.id),
        label: (
          <div className="history-changeset-header">
            <div className="history-changeset-header-label flex flex-column">
              <span className="body-s-medium">
                {changeset.createdBy.fullName}{" "}
                <span className="body-s-regular">
                  {t("datablocks.labels.madeChanges", {
                    count: changeset.changes.length,
                  })}
                </span>
              </span>

              <span className="history-changeset-header-date body-xs-regular">
                {DateTime.fromISO(changeset.createdAt).toFormat("dd MMMM yyyy")}
              </span>
            </div>
          </div>
        ),
        children: (
          <div className="history-changeset-body flex flex-col gap-300">
            {changeset.changes.map((change) => (
              <HistoryChangeRow
                key={change.id}
                change={change}
                lookup={lookup}
                createdBy={changeset.createdBy}
                createdAt={changeset.createdAt}
              />
            ))}
          </div>
        ),
      })),
    [sortedChangesets, t, lookup],
  );

  if (isLoading || isRelatedLoading) {
    return <BaseSpinner size="small" />;
  }

  if (!sortedChangesets.length) {
    return (
      <div className="history-empty flex flex-col gap-100 items-center justify-center">
        <div className="history-empty-icon flex items-center justify-center">
          <HistoryIcon color="var(--neutral-600)" />
        </div>

        <span className="body-m-medium">
          {t("datablocks.labels.noHistory")}
        </span>

        <span className="history-empty-description body-m-regular">
          {t("datablocks.labels.noHistoryDescription")}
        </span>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-timeline">
        {sortedChangesets.map((changeset, index) => {
          const item = accordionItems[index];
          if (!item) return;

          return (
            <div
              key={changeset.id}
              className="history-timeline-item flex gap-200"
            >
              <div className="history-timeline-node">
                <div className="history-timeline-avatar">
                  <UserAvatar user={changeset.createdBy} />
                </div>

                {index < sortedChangesets.length - 1 && (
                  <div className="history-timeline-connector" />
                )}
              </div>

              <div className="history-timeline-content full-width">
                <BaseAccordion
                  className="history-accordion"
                  variant={AccordionVariant.TRANSPARENT}
                  expandIconPosition="end"
                  items={[item]}
                />
              </div>
            </div>
          );
        })}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="history-sentinel">
          {isFetching && <BaseSpinner size="small" />}
        </div>
      )}
    </div>
  );
};

export default History;
